import Image from '@plone/volto/components/theme/Image/Image';
import ploneLogo from 'volto-uni-bonn/assets/plone-logo.svg';
// Copied from lino-design-systems/src/layout/Header.tsx; adapted to Volto.
import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SearchWidget from '@plone/volto/components/theme/SearchWidget/SearchWidget';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';
import { getNavroot } from '@plone/volto/actions/navroot/navroot';
import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  MenuIcon,
  SearchIcon,
} from 'volto-uni-bonn/components/SiteChrome/icons';
import useDesktopLayout from 'volto-uni-bonn/components/SiteChrome/useDesktopLayout';
import 'volto-uni-bonn/theme/header.css';
const ICON_SIZE = 16;
const ICON_SIZE_MENU = 24;
const ICON_SIZE_TOGGLE = 29;
const ICON_SIZE_DROPDOWN = 13;
function LanguageLinks({ current, languages }) {
  return languages.map((language) => (
    <li key={language.label}>
      <UniversalLink
        href={language.url}
        lang={language.label}
        aria-current={language.label === current ? 'true' : undefined}
      >
        {language.label}
      </UniversalLink>
    </li>
  ));
}
function LanguageDropdown({ current, languages }) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  return (
    <div className="bonn-lang">
      {languages.length === 1 ? (
        <span className="bonn-lang__toggle">{current}</span>
      ) : (
        <>
          <button
            type="button"
            className="bonn-lang__toggle"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            <span>{current}</span>
            <span
              className={
                open
                  ? 'bonn-lang__arrow bonn-lang__arrow--open'
                  : 'bonn-lang__arrow'
              }
            >
              <ChevronDownIcon
                size={ICON_SIZE_DROPDOWN}
                title="Choose language"
              />
            </span>
          </button>
          <ul id={listId} className="bonn-lang__list" hidden={!open}>
            <LanguageLinks current={current} languages={languages} />
          </ul>
        </>
      )}
    </div>
  );
}
function Menu({
  id,
  open,
  items,
  currentLanguage,
  languages,
  home,
  search,
  onClose,
}) {
  const dialog = useRef(null);
  useEffect(() => {
    if (!open) return;
    const node = dialog.current;
    node.showModal();
    document.body.classList.add('bonn-menu-open');
    return () => {
      node.close();
      document.body.classList.remove('bonn-menu-open');
    };
  }, [open]);
  return (
    <dialog
      ref={dialog}
      id={id}
      className="bonn-menu"
      aria-label="Navigation"
      onCancel={onClose}
    >
      <div className="bonn-menu__header">
        <UniversalLink href={home} className="bonn-menu__logo">
          <Image
            className="conference-plone-logo"
            src={ploneLogo}
            alt="Plone"
          />
        </UniversalLink>
        <div className="bonn-menu__actions">
          <UniversalLink href={search} className="bonn-menu__action">
            <SearchIcon size={ICON_SIZE_MENU} title="Search" />
          </UniversalLink>
          <button type="button" className="bonn-menu__action" onClick={onClose}>
            <CloseIcon size={ICON_SIZE_MENU} title="Close navigation" />
          </button>
        </div>
      </div>
      <nav className="bonn-menu__nav" aria-label="Main navigation">
        <ul>
          {items.map((item) => (
            <li key={item.url}>
              <NavItem item={item} lang={currentLanguage} />
              <ChevronRightIcon size={ICON_SIZE_MENU} />
            </li>
          ))}
        </ul>
      </nav>
      <ul className="bonn-menu__languages" aria-label="Language">
        <LanguageLinks current={currentLanguage} languages={languages} />
      </ul>
    </dialog>
  );
}
export default function Header({ pathname }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const items =
    useSelector((state) => state.navigation.items, shallowEqual) || [];
  const currentLanguage = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession.token);
  const site = useSelector((state) => state.site.data);
  const content = useSelector((state) => state.content.data);
  const navroot = useSelector((state) => state.navroot.data);
  const home = flattenToAppURL(navroot?.navroot?.['@id']) || '/';
  const search = `${home.replace(/\/$/, '')}/search`;
  const languages = (
    site.features?.multilingual
      ? site['plone.available_languages']
      : [currentLanguage]
  ).map((language) => {
    const translation = content?.['@components']?.translations?.items?.find(
      (item) => item.language === language,
    );
    return {
      label: language,
      url: translation
        ? flattenToAppURL(translation['@id'])
        : language === currentLanguage
          ? getBaseUrl(pathname)
          : `/${language}`,
    };
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const isDesktop = useDesktopLayout();
  useEffect(() => {
    if (!hasApiExpander('navroot', getBaseUrl(pathname))) {
      dispatch(getNavroot(getBaseUrl(pathname)));
    }
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.key, isDesktop]);
  return (
    <header id="navigation" tabIndex="-1" className="bonn-header">
      <div className="ui container bonn-header__bar">
        <UniversalLink className="bonn-header__logo" href={home}>
          <Image
            className="conference-plone-logo"
            src={ploneLogo}
            alt="Plone"
          />
        </UniversalLink>
        {!isDesktop && languages.length > 1 && (
          <LanguageDropdown current={currentLanguage} languages={languages} />
        )}
        <nav className="bonn-nav" aria-label="Main navigation">
          <ul className="bonn-nav__list">
            {items.map((item) => (
              <li key={item.url}>
                <NavItem item={item} lang={currentLanguage} />
              </li>
            ))}
            <li>
              <UniversalLink
                className="bonn-nav__link bonn-nav__link--icon"
                href={search}
              >
                <SearchIcon size={ICON_SIZE} title="Search" />
              </UniversalLink>
            </li>
          </ul>
        </nav>
        <button
          type="button"
          className="bonn-header__toggle"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => {
            setMenuOpen(true);
          }}
        >
          <MenuIcon size={ICON_SIZE_TOGGLE} title="Open navigation" />
        </button>
      </div>
      {location.pathname.endsWith('/search') && (
        <div className="ui container bonn-header__search">
          <SearchWidget />
        </div>
      )}
      {!isDesktop && (
        <Menu
          id={menuId}
          open={menuOpen}
          items={items}
          languages={languages}
          home={home}
          search={search}
          currentLanguage={currentLanguage}
          onClose={() => {
            setMenuOpen(false);
          }}
        />
      )}
    </header>
  );
}
